import { getApiErrorMessage } from "@core/utility/get-error-message";
import {
  createScheduleExceptionController,
  CreateScheduleExceptionModel,
  deleteScheduleExceptionController,
  DeleteScheduleExceptionModel,
  getNextAvailableDateRestrictedController,
  getPublicNextAvailableDateController,
  getPublicUpcomingExceptionsController,
  GetResponseScheduleExceptionModel,
  getScheduleExceptionController,
  GetScheduleExceptionModel,
  getUpcomingExceptionsController,
  MutateResponseScheduleExceptionModel,
  QueryKeyScheduleException,
  updateScheduleExceptionController,
  UpdateScheduleExceptionModel,
} from "@factory/model/query-model-schedule-exception";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { AxiosError } from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { authHeaders } from "../authentication/auth-header";
import { useSessionContext } from "../authentication/session.context";

export function useQyGetScheduleException(
  search: string,
  limit = 99,
  offset = 0,
  order?: object,
  filter?: Record<string, string>,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetResponseScheduleExceptionModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (
    search: string | undefined = undefined,
    limit: number | undefined = undefined,
    offset: number | undefined = undefined,
    order: object | undefined = undefined,
    filter: Record<string, string> | undefined = undefined,
  ) => {
    await checkSession();

    showProgress();
    const operation = await getScheduleExceptionController(
      search,
      limit,
      offset,
      order,
      { filter: JSON.stringify(filter) },
      undefined,
      undefined,
      undefined,
      authHeaders(),
    );
    const response = (await operation()).data;
    return response as GetResponseScheduleExceptionModel;
  };

  return useQuery({
    enabled,
    queryKey: [QueryKeyScheduleException, search, limit, offset, order, filter],
    queryFn: () => apiFn(search, limit, offset, order, filter),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetUpcomingExceptions(
  tenant: string,
  limit?: number,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetScheduleExceptionModel[]) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (tenant: string, limit?: number) => {
    await checkSession();

    showProgress();
    const operation = await getUpcomingExceptionsController(
      tenant,
      limit,
      authHeaders(),
    );
    const response = (await operation()).data;
    return response.data as GetScheduleExceptionModel[];
  };

  return useQuery({
    enabled: enabled !== false && !!tenant,
    queryKey: [QueryKeyScheduleException, "upcoming", tenant, limit],
    queryFn: () => apiFn(tenant, limit),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetPublicUpcomingExceptions(
  tenant: string,
  limit?: number,
  enabled?: boolean,
  onSuccess?:
    | ((data: GetScheduleExceptionModel[]) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: AxiosError) => void | Promise<unknown>) | undefined,
) {
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (tenant: string, limit?: number) => {
    showProgress();
    const operation = await getPublicUpcomingExceptionsController(
      tenant,
      limit,
    );
    const response = (await operation()).data;
    return response.data as GetScheduleExceptionModel[];
  };

  return useQuery({
    enabled: enabled !== false && !!tenant,
    queryKey: [QueryKeyScheduleException, "public-upcoming", tenant, limit],
    queryFn: () => apiFn(tenant, limit),
    onSuccess: (response) => {
      hideProgress();
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export interface NextAvailableDateResponse {
  next_available_date: string;
  default_date: string;
}

export function useQyGetPublicNextAvailableDate(
  tenant: string,
  enabled?: boolean,
) {
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (tenant: string) => {
    showProgress();
    const operation = await getPublicNextAvailableDateController(tenant);
    const response = (await operation()).data;
    return response.data as unknown as NextAvailableDateResponse;
  };

  return useQuery({
    enabled: enabled !== false && !!tenant,
    queryKey: [QueryKeyScheduleException, "public-next-available", tenant],
    queryFn: () => apiFn(tenant),
    onSuccess: () => {
      hideProgress();
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyGetNextAvailableDateRestricted(
  tenant: string,
  enabled?: boolean,
) {
  const { checkSession } = useSessionContext();
  const { showProgress, hideProgress, showError } = useNotificationContext();

  const apiFn = async (tenant: string) => {
    await checkSession();

    showProgress();
    const operation = await getNextAvailableDateRestrictedController(
      tenant,
      authHeaders(),
    );
    const response = (await operation()).data;
    return response.data as unknown as NextAvailableDateResponse;
  };

  return useQuery({
    enabled: enabled !== false && !!tenant,
    queryKey: [QueryKeyScheduleException, "next-available", tenant],
    queryFn: () => apiFn(tenant),
    onSuccess: () => {
      hideProgress();
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyCreateScheduleException(
  onSuccess?:
    | ((data: MutateResponseScheduleExceptionModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError, showSuccess } =
    useNotificationContext();

  const apiFn = async (payload: CreateScheduleExceptionModel) => {
    await checkSession();

    showProgress();
    const operation = await createScheduleExceptionController(
      payload,
      authHeaders(),
    );
    const response = (await operation()).data;
    return response as MutateResponseScheduleExceptionModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyScheduleException);
      showSuccess("Schedule exception created successfully");
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyUpdateScheduleException(
  onSuccess?:
    | ((data: MutateResponseScheduleExceptionModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError, showSuccess } =
    useNotificationContext();

  const apiFn = async (payload: UpdateScheduleExceptionModel) => {
    await checkSession();

    showProgress();
    const operation = await updateScheduleExceptionController(
      payload,
      authHeaders(),
    );
    const response = (await operation()).data;
    return response as MutateResponseScheduleExceptionModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyScheduleException);
      showSuccess("Schedule exception updated successfully");
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}

export function useQyDeleteScheduleException(
  onSuccess?:
    | ((data: MutateResponseScheduleExceptionModel) => void | Promise<unknown>)
    | undefined,
  onError?: ((error: unknown) => void | Promise<unknown>) | undefined,
) {
  const { checkSession } = useSessionContext();
  const queryClient = useQueryClient();
  const { showProgress, hideProgress, showError, showSuccess } =
    useNotificationContext();

  const apiFn = async (payload: DeleteScheduleExceptionModel) => {
    await checkSession();

    showProgress();
    const operation = await deleteScheduleExceptionController(
      payload,
      authHeaders(),
    );
    const response = (await operation()).data;
    return response as MutateResponseScheduleExceptionModel;
  };

  return useMutation({
    mutationFn: apiFn,
    onSuccess: (response) => {
      hideProgress();
      queryClient.invalidateQueries(QueryKeyScheduleException);
      showSuccess("Schedule exception deleted successfully");
      if (onSuccess) {
        onSuccess(response);
      }
    },
    onError: (err: AxiosError) => {
      hideProgress();
      const message = getApiErrorMessage(err);
      showError(message);

      if (onError) {
        onError(err);
      }
    },
    onSettled() {
      hideProgress();
    },
  });
}
